/* eslint-disable react/jsx-props-no-spreading */
import AddCircleIcon from "@mui/icons-material/AddCircle"
import { Box, Grid, Stack, TextField, Typography } from "@mui/material"
import {
    GridColDef,
    GridPaginationModel,
    GridRowParams,
} from "@mui/x-data-grid"
import { ReactElement, useEffect, useMemo, useState } from "react"

import {
    COMPLIANCE_STATUS_KW,
    COMPLIANT,
    DESC,
    DEVICE_MODEL_FILTER_KW,
    MAX_CAPACITY_KW,
    MIN_CAPACITY_KW,
    ORDER_BY_KW,
    ORDER_KW,
    TTR_DEFAULT_ORDER_BY,
} from "../../../constants/generalConstants"
import useMultiFetcherGenFilterReferenceData from "../../../services/middleware/hooks/useMultiFetcherGenFilterReference"
import { useSearchGenDevicesPage } from "../../../services/middleware/hooks/useMultiFetcherSearchGenDevices"
import {
    CATEGORY_ID_URL_PARAM,
    DEVICE_TYPE_ID_URL_PARAM,
    MANUFACTURER_ID_URL_PARAM,
    PHASE_REF_KEY,
    TComplianceStatusRef,
    TDeviceCatRef,
    TDeviceTypes,
    TManufacturerRef,
    TPhasesRef,
} from "../../../services/middleware/mappers/genFilterReferenceMapper"
import { GRID_SPACING } from "../../../themes/defaultThemeParams"
import { MAX_CAPACITY } from "../../../webpages/lowCarbonTechDatabases/genDeviceSearch"
import AdminDataGrid, {
    CustomAction,
} from "../../dataDisplay/adminDataGrid/adminDataGrid"
import AppControlledSelect from "../../inputs/controlled/appControlledSelect"
import { IDatabaseSearchResult as IGenDatabaseSearchResult } from "../../tables/ttr/genSearchResults"

export const GEN_PANEL_PAGE_SIZE = 20

export type GenLockedFilters = {
    /** When set, restricts the search to this device category and hides the
     * (otherwise non-existent) category control. */
    categoryId?: string | number
    /** When set, restricts the search to this phase and hides the phase
     * control. */
    phaseId?: string | number
}

export interface GenDeviceSearchPanelProps {
    /** Restrictions imposed by the caller (e.g. application form's selected
     * LCT, v2g bundle's inverter category). */
    locked?: GenLockedFilters
    /** Fired when the user picks a row. Receives the raw search result; the
     * caller is responsible for any formatting. */
    onSelect: (row: IGenDatabaseSearchResult) => void
    /** databaseId of a row to render as visually selected, if any. */
    selectedDatabaseId?: number | null
    /** When true, always hit the public search route + public client even if signed in */
    forcePublicRoute?: boolean
}

const columns: GridColDef[] = [
    {
        field: "deviceReference",
        headerName: "System Reference",
        flex: 1.4,
        minWidth: 180,
        sortable: false,
    },
    {
        field: "datePublished",
        headerName: "Date Published",
        flex: 1,
        minWidth: 130,
        sortable: false,
    },
    {
        field: "manufacturer",
        headerName: "Manufacturer",
        flex: 1.2,
        sortable: false,
    },
    { field: "model", headerName: "Model", flex: 1.2, sortable: false },
    { field: "type", headerName: "Type", flex: 1.4, sortable: false },
    {
        field: "capacity",
        headerName: "Capacity (kW)",
        flex: 1,
        sortable: false,
    },
    { field: "phases", headerName: "Phase", flex: 0.8, sortable: false },
]

/**
 * Reusable panel for searching generation devices. State is local — no URL
 * sync, no redux — so the panel can live equally well inline (e.g. v2g
 * bundle creation) or wrapped in a dialog (application form picker).
 */
const GenDeviceSearchPanel = ({
    locked,
    onSelect,
    selectedDatabaseId,
    forcePublicRoute,
}: GenDeviceSearchPanelProps): ReactElement => {
    const { referenceData, isLoading: referenceLoading } =
        useMultiFetcherGenFilterReferenceData(true)

    const [manufacturerId, setManufacturerId] = useState<string>("")
    const [deviceTypeId, setDeviceTypeId] = useState<string>("")
    const [phaseId, setPhaseId] = useState<string>("")
    const [deviceModel, setDeviceModel] = useState<string>("")
    const [capacityMin, setCapacityMin] = useState<string>("")
    const [capacityMax, setCapacityMax] = useState<string>("")
    const [paginationModel, setPaginationModel] = useState<GridPaginationModel>(
        {
            page: 0,
            pageSize: GEN_PANEL_PAGE_SIZE,
        }
    )

    const compliantStatusId = useMemo(() => {
        const match = referenceData?.complianceStatuses?.find(
            (status: TComplianceStatusRef) => status.StatusShort === COMPLIANT
        )
        return match?.ComplianceStatusID
    }, [referenceData])

    const lockedCategoryName = useMemo(() => {
        if (!locked?.categoryId) return undefined
        const match = referenceData?.deviceCategories?.find(
            (cat: TDeviceCatRef) =>
                String(cat.DeviceCategoryID) === String(locked.categoryId)
        )
        return match?.DeviceCategory
    }, [locked?.categoryId, referenceData])

    const lockedPhaseName = useMemo(() => {
        if (!locked?.phaseId) return undefined
        const match = referenceData?.phase_id?.find(
            (phase: TPhasesRef) =>
                String(phase.PhaseID) === String(locked.phaseId)
        )
        return match?.Phase
    }, [locked?.phaseId, referenceData])

    const searchParams = useMemo(() => {
        const params = new URLSearchParams()

        // Default ordering: most recently published first. The gen search
        // hook already defaults these when missing, but we set them
        // explicitly so the panel's intent is clear and so the params used
        // here match what the data grid header indicates.
        params.set(ORDER_BY_KW, TTR_DEFAULT_ORDER_BY)
        params.set(ORDER_KW, DESC)

        if (locked?.categoryId !== undefined) {
            params.set(CATEGORY_ID_URL_PARAM, String(locked.categoryId))
        }
        if (locked?.phaseId !== undefined) {
            params.set(PHASE_REF_KEY, String(locked.phaseId))
        } else if (phaseId) {
            params.set(PHASE_REF_KEY, phaseId)
        }
        if (compliantStatusId !== undefined) {
            params.set(COMPLIANCE_STATUS_KW, String(compliantStatusId))
        }
        if (manufacturerId) {
            params.set(MANUFACTURER_ID_URL_PARAM, manufacturerId)
        }
        if (deviceTypeId) {
            params.set(DEVICE_TYPE_ID_URL_PARAM, deviceTypeId)
        }
        if (deviceModel) {
            params.set(DEVICE_MODEL_FILTER_KW, deviceModel)
        }
        if (capacityMin) {
            params.set(MIN_CAPACITY_KW, capacityMin)
        }
        if (capacityMax) {
            params.set(MAX_CAPACITY_KW, capacityMax)
        }

        return params
    }, [
        locked?.categoryId,
        locked?.phaseId,
        compliantStatusId,
        manufacturerId,
        deviceTypeId,
        phaseId,
        deviceModel,
        capacityMin,
        capacityMax,
    ])

    const { data, isLoading } = useSearchGenDevicesPage(
        searchParams,
        paginationModel.page,
        GEN_PANEL_PAGE_SIZE,
        forcePublicRoute
    )

    const [rowCount, setRowCount] = useState(0)
    useEffect(() => {
        if (data?.totalResults !== undefined) {
            setRowCount(data.totalResults)
        }
    }, [data])

    useEffect(() => {
        setPaginationModel((prev) => ({ ...prev, page: 0 }))
    }, [searchParams])

    const customActions: CustomAction<IGenDatabaseSearchResult>[] = useMemo(
        () => [
            {
                key: "select-gen-device",
                label: "Select",
                icon: (
                    <AddCircleIcon aria-label="select" sx={{ fontSize: 32 }} />
                ),
                onClick: (row: IGenDatabaseSearchResult) => onSelect(row),
                tooltip: "Select this device",
            },
        ],
        [onSelect]
    )

    const restrictionLines: string[] = []
    if (lockedCategoryName) {
        restrictionLines.push(`Category: ${lockedCategoryName}`)
    }
    if (lockedPhaseName) {
        restrictionLines.push(`Phase: ${lockedPhaseName}`)
    }

    const rows = data?.data ?? []

    return (
        <Stack spacing={GRID_SPACING * 2}>
            {restrictionLines.length > 0 && (
                <Typography color="text.secondary">
                    Search restricted to — {restrictionLines.join(" \u00B7 ")}.
                </Typography>
            )}
            <Grid container spacing={GRID_SPACING}>
                <Grid item xs={12} md={4}>
                    <AppControlledSelect
                        fullWidth
                        options={
                            referenceData?.deviceManufacturers?.map(
                                (m: TManufacturerRef) =>
                                    String(m.ManufacturerID)
                            ) ?? []
                        }
                        value={manufacturerId || null}
                        loading={referenceLoading}
                        setValue={(_event, value) =>
                            setManufacturerId(String(value ?? ""))
                        }
                        getOptionLabel={(option) =>
                            referenceData?.deviceManufacturers?.find(
                                (m: TManufacturerRef) =>
                                    String(m.ManufacturerID) === String(option)
                            )?.Manufacturer ?? String(option)
                        }
                        label="Manufacturer"
                    />
                </Grid>
                <Grid item xs={12} md={4}>
                    <AppControlledSelect
                        fullWidth
                        options={
                            referenceData?.deviceTypes?.map((t: TDeviceTypes) =>
                                String(t.DeviceTypeID)
                            ) ?? []
                        }
                        value={deviceTypeId || null}
                        loading={referenceLoading}
                        setValue={(_event, value) =>
                            setDeviceTypeId(String(value ?? ""))
                        }
                        getOptionLabel={(option) =>
                            referenceData?.deviceTypes?.find(
                                (t: TDeviceTypes) =>
                                    String(t.DeviceTypeID) === String(option)
                            )?.DeviceType ?? String(option)
                        }
                        label="Type"
                    />
                </Grid>
                {locked?.phaseId === undefined && (
                    <Grid item xs={12} md={4}>
                        <AppControlledSelect
                            fullWidth
                            options={
                                referenceData?.phase_id?.map((p: TPhasesRef) =>
                                    String(p.PhaseID)
                                ) ?? []
                            }
                            value={phaseId || null}
                            loading={referenceLoading}
                            setValue={(_event, value) =>
                                setPhaseId(String(value ?? ""))
                            }
                            getOptionLabel={(option) =>
                                referenceData?.phase_id?.find(
                                    (p: TPhasesRef) =>
                                        String(p.PhaseID) === String(option)
                                )?.Phase ?? String(option)
                            }
                            label="Phase"
                        />
                    </Grid>
                )}
                <Grid item xs={12} md={4}>
                    <TextField
                        fullWidth
                        label="Model"
                        value={deviceModel}
                        onChange={(event) => setDeviceModel(event.target.value)}
                    />
                </Grid>
                <Grid item xs={12} md={4}>
                    <TextField
                        fullWidth
                        label="Minimum Capacity (kW)"
                        type="number"
                        value={capacityMin}
                        onChange={(event) => setCapacityMin(event.target.value)}
                    />
                </Grid>
                <Grid item xs={12} md={4}>
                    <TextField
                        fullWidth
                        label="Maximum Capacity (kW)"
                        type="number"
                        value={capacityMax}
                        inputProps={{ min: 0, max: MAX_CAPACITY }}
                        onChange={(event) => setCapacityMax(event.target.value)}
                    />
                </Grid>
            </Grid>

            <Box>
                <AdminDataGrid<IGenDatabaseSearchResult>
                    rows={rows}
                    columns={columns}
                    getRowId={(row: IGenDatabaseSearchResult) =>
                        String(row.databaseId)
                    }
                    itemTypeText="device"
                    isLoading={isLoading || referenceLoading}
                    showActions
                    customActions={customActions}
                    hideToolbar
                    showTitle={false}
                    disableRowSelectionOnClick
                    paginationMode="server"
                    rowCount={rowCount}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    pageSizeOptions={[GEN_PANEL_PAGE_SIZE]}
                    getRowClassName={(params: GridRowParams) =>
                        selectedDatabaseId !== undefined &&
                        selectedDatabaseId !== null &&
                        params.row.databaseId === selectedDatabaseId
                            ? "row-selected"
                            : ""
                    }
                />
            </Box>
        </Stack>
    )
}

GenDeviceSearchPanel.defaultProps = {
    locked: undefined,
    selectedDatabaseId: null,
    forcePublicRoute: false,
}

export default GenDeviceSearchPanel
